// Generic import
import _ from 'lodash';
import xmlescape from 'xml-escape';

// Local import
import { calcChargeableWeight, calcTotalVolume, calcWeight, calcWeightOrMeasurment } from './CalcWeight.js';
import { calcFieldValue } from './utils/opportunity.js';
import readableId from './ReadableId.js';
import CURRENCIES_NAME from './CWXml.currencies.js';
import { getClassNumber } from './utils/Dangerous.js';
import { getTotalRates } from './dashboard/utils/rates.js';

function createXmlStructureFromOpportunity({ organization, opportunity, account, me, suppliers, containerModeIndex, toggleContainerModeIndex, dataTargetType, exchangeRates, lockedExchangeRates }) {

    const { transportationMethod, products } = opportunity;

    const offerLink = `https://app.monada.ai/organization/${organization.alias}/opportunities/${opportunity.id}`;

    const lastStatus = opportunity.status;
    const lastStatusChange = (() => {
        // A newly created opportunity that hasn't been saved/synced yet has no `log` —
        // guard against it so opening Preview doesn't crash on `log.length`.
        const log = opportunity.log || [];
        let entry = null;
        if (lastStatus === 'endcustomer-accepted') entry = log.findLast(l => l.action ==='accepted');
        if (lastStatus === 'endcustomer-rejected') entry = log.findLast(l => l.action ==='rejected');
        if (lastStatus === 'won') entry = log.findLast(l => l.action ==='marked-won');
        if (lastStatus === 'lost') entry = log.findLast(l => l.action ==='marked-lost');
        if (!entry) entry = log[log.length - 1];
        // Fall back to the creation time (or now) so downstream `new Date(lastStatusChange)`
        // stays valid for a brand-new opportunity that has no status-change log yet.
        return entry?.timestamp ?? opportunity.created ?? Date.now();
    })();

    const selectedOffer = (opportunity.offers || []).find(o => (opportunity.selected || []).includes(o.id)) || (opportunity.offers || [])[0];

    const costs = {};

    const [ branchCode, branchName, accountUniqueId ] = (() => {
        if ((account.uniqueId || '').split(':').length > 1) return (account.uniqueId || '').split(':');
        return ['', '', account.uniqueId];
    })();

    _.forEach(selectedOffer?.sections, (section, sectionIndex) => {
        const selectedSectionOffer = section.offers.find(o => (opportunity.selected || []).includes(o.id)) || section.offers[0];
        _.forEach(selectedSectionOffer.fields, field => {
            // TODO ? Text
            if (field.type !== 'string') {
                const buyingSum = getTotalRates({ field, fieldBuyingRates: field.buyingRates, products, transportationMethod, buyingRates: opportunity.buyingRates, exchangeRates, skipSumCurrencies: true, organization });
                let sum = calcFieldValue({ field, sections: selectedOffer?.sections, products, transportationMethod, organization, lockedExchangeRates, buyingRates: opportunity.buyingRates, fieldBuyingRates: field.buyingRates, exchangeRates });

                const buyingCreditors = _.compact((field.buyingRates || []).map(br => {
                    const rate = (opportunity.buyingRates || []).find(r => r.id === br.rateId);
                    if (!rate) return null;
                    return suppliers?.find(s => s.name === rate.supplier.organization);
                }));

                if (!_.isEmpty(buyingSum) && !sum) {
                    sum = { sum: {
                        [_.first(_.keys(buyingSum))]: 0
                    }};
                }

                if (!sum) return;

                const title = `${section.title}|${field.title}`;
                const cargowiseChargeCode = field.cargowiseChargeCode || '-NA-';
                costs[cargowiseChargeCode] = costs[cargowiseChargeCode] || {};
                costs[cargowiseChargeCode][title] = costs[cargowiseChargeCode][title] || {};
                costs[cargowiseChargeCode][title].buyingCreditors = buyingCreditors;

                _.forEach(sum.sum, (value, currency) => {
                    costs[cargowiseChargeCode][title].selling = costs[cargowiseChargeCode][title].selling || {};
                    costs[cargowiseChargeCode][title].selling[currency] = costs[cargowiseChargeCode][title].selling[currency] || 0;
                    costs[cargowiseChargeCode][title].selling[currency] = costs[cargowiseChargeCode][title].selling[currency] + value;
                });

                _.forEach(buyingSum, (value, currency) => {
                    costs[cargowiseChargeCode][title].buying = costs[cargowiseChargeCode][title].buying || {};
                    costs[cargowiseChargeCode][title].buying[currency] = costs[cargowiseChargeCode][title].buying[currency] || 0;
                    costs[cargowiseChargeCode][title].buying[currency] = costs[cargowiseChargeCode][title].buying[currency] + value;
                });
            }
        })
    })

    const structure = {
        name: 'UniversalShipment',
        attributes: [{
            name: 'xmlns',
            value: 'http://www.cargowise.com/Schemas/Universal/2011/11'
        }, {
            name: 'version',
            value: '1.1'
        }],
        children: [{
            name: 'Shipment',
            children: _.compact([{
                name: 'DataContext',
                children: [{
                    name: 'DataTargetCollection',
                    children: [{
                        name: 'DataTarget',
                        children: [{
                            name: 'Type',
                            value: dataTargetType || 'ForwardingShipment',
                        }, {
                            name: 'Key',
                        }],
                    }],
                }, {
                    name: 'Company',
                    children: [{
                        name: 'Code',
                        value: (organization.integrations.cargowise || {}).companyCode || '',
                    }],
                }, {
                    name: 'EnterpriseID',
                    value: (organization.integrations.cargowise || {}).enterpriseId || '',
                }, {
                    name: 'ServerID',
                    value: (organization.integrations.cargowise || {}).serverId || '',
                }],
            }, {
                name: 'Branch',
                children: [{
                    name: 'Code',
                    value: branchCode,
                }]
            }, {
                name: 'TransportMode',
                children: [{
                    name: 'Code',
                    value: TRANSPORT_MODE_CODE[transportationMethod],
                }, {
                    name: 'Description',
                    value: TRANSPORT_MODE_DESCRIPTION[transportationMethod],
                }]
            }, {
                name: 'ContainerMode',
                children: [{
                    name: 'Code',
                    value: CONTAINER_MODES[transportationMethod][containerModeIndex].code,
                    onClick: toggleContainerModeIndex
                }, {
                    name: 'Description',
                    value: CONTAINER_MODES[transportationMethod][containerModeIndex].description,
                }]
            }, {
                name: 'GoodsDescription',
                value: 'General Commodity',
            }, {
                name: 'LloydsIMO',
            }, {
                name: 'OuterPacks',
            }, {
                name: 'OuterPacksPackageType',
                children: [{
                    name: 'Code',
                }, {
                    name: 'Description',
                }]
            }, {
                name: 'PackingOrder',
                value: '0',
            }, (opportunity.routes?.[0]?.source?.type === 'port' ? {
                name: 'PortOfOrigin',
                children: [{
                    name: 'Code',
                    value: opportunity.routes[0].source.id,
                }, {
                    name: 'Name',
                    value: opportunity.routes[0].source.text,
                }]
            } : null), (opportunity.routes && opportunity.routes[opportunity.routes.length - 1]?.destination?.type === 'port' ? {
                name: 'PortOfDestination',
                children: [{
                    name: 'Code',
                    value: opportunity.routes[opportunity.routes.length - 1].destination.id,
                }, {
                    name: 'Name',
                    value: opportunity.routes[opportunity.routes.length - 1].destination.text,
                }]
            } : null), false ? {
                name: 'TransportLegCollection',
                children: opportunity.routes.map((r, i) => ({
                    name: 'TransportLeg',
                    children: _.compact([r.source.type === 'port' ? {
                        name: 'PortOfLoading',
                        children: [{
                            name: 'Code',
                            value: r.source.id,
                        }, {
                            name: 'Name',
                            value: r.source.text,
                        }]
                    } : null, r.destination.type === 'port' ? {
                        name: 'PortOfDischarge',
                        children: [{
                            name: 'Code',
                            value: r.destination.id,
                        }, {
                            name: 'Name',
                            value: r.destination.text,
                        }]
                    } : null, {
                        name: 'LegOrder',
                        value: i
                    }])
                })),
            } : null, {
                name: 'CustomizedFieldCollection',
                children: [{
                    name: 'CustomizedField',
                    children: [{
                        name: 'DataType',
                        value: 'String',
                    }, {
                        name: 'Key',
                        value: 'Monada Quote Ref',
                    }, {
                        name: 'Value',
                        value: readableId(opportunity),
                    }]
                }, {
                    name: 'CustomizedField',
                    children: [{
                        name: 'DataType',
                        value: 'DateTime',
                    }, {
                        name: 'Key',
                        value: 'Task Creation in Monada',
                    }, {
                        name: 'Value',
                        value: new Date(opportunity.created || 0).toISOString(),
                    }]
                }, {
                    name: 'CustomizedField',
                    children: [{
                        name: 'DataType',
                        value: 'DateTime',
                    }, {
                        name: 'Key',
                        value: `Task Marked as ${lastStatus === 'completed' ? 'Won' : lastStatus === 'lost' ? 'Lost' : (lastStatus ?? 'null')}`,
                    }, {
                        name: 'Value',
                        value: new Date(lastStatusChange).toISOString(),
                    }]
                }]
            }, {
                name: 'NoteCollection',
                atrributes: [{ name: 'Content', value: 'Partial' }],
                children: [{
                    name: 'Note',
                    children: [{
                        name: 'Description',
                        value: 'Monada Notes (Operator to Read)',
                    }, {
                        name: 'IsCustomNote',
                        value: 'true',
                    }, {
                        name: 'IsCustomDescription',
                        value: 'true',
                    }, {
                        name: 'NoteText',
                        value: offerLink,
                    }]
                }, {
                    name: 'Note',
                    children: [{
                        name: 'Description',
                        value: 'Monada Notes: Carriers\' Remarks',
                    }, {
                        name: 'IsCustomNote',
                        value: 'true',
                    }, {
                        name: 'IsCustomDescription',
                        value: 'true',
                    }, {
                        name: 'NoteText',
                        value: (opportunity.buyingRates || []).map(r => r.notes || '').join('\n')
                    }]
                }]
            }, {
                name: 'DateCollection',
                children: [{
                    name: 'Date',
                    children: [{
                        name: 'Type',
                        value: 'BookingConfirmed',
                    }, {
                        name: 'IsEstimate',
                        value: 'false',
                    }, {
                        name: 'Value',
                        value: new Date(lastStatusChange).toISOString(),
                    }]
                }, {
                    name: 'Date',
                    children: [{
                        name: 'Type',
                        value: 'Departure',
                    }, {
                        name: 'IsEstimate',
                        value: 'true',
                    }, {
                        name: 'Value',
                        value: new Date(opportunity.created || 0).toISOString(),
                    }]
                }, {
                    name: 'Date',
                    children: [{
                        name: 'Type',
                        value: 'Arrival',
                    }, {
                        name: 'IsEstimate',
                        value: 'true',
                    }, {
                        name: 'Value',
                        value: new Date(opportunity.created || 0).toISOString(),
                    }]
                }]
            }, {
                name: 'OrganizationAddressCollection',
                children: [{
                    name: 'OrganizationAddress',
                    children: [{
                        name: 'AddressType',
                        value: dataTargetType === 'ForwardingShipment' ? 'ConsigneeDocumentaryAddress' : 'OneOffQuoteDeliveryAddress',
                    }, {
                        name: 'Address1',
                        value: ''
                    }, {
                        name: 'Address2',
                        value: ''
                    }, {
                        name: 'AddressOverride',
                        value: 'false'
                    }, {
                        name: 'AddressShortCode',
                        value: ''
                    }, {
                        name: 'City',
                        value: ''
                    }, {
                        name: 'CompanyName',
                        value: 'Monada'
                    }, {
                        name: 'OrganizationCode',
                        value: 'MONADATLV'
                    }]
                }, {
                    name: 'OrganizationAddress',
                    children: [{
                        name: 'AddressType',
                        value: dataTargetType === 'ForwardingShipment' ? 'ConsignorDocumentaryAddress' : 'OneOffQuotePickupAddress',
                    }, {
                        name: 'Address1',
                        value: ''
                    }, {
                        name: 'Address2',
                        value: ''
                    }, {
                        name: 'AddressOverride',
                        value: 'false'
                    }, {
                        name: 'AddressShortCode',
                        value: ''
                    }, {
                        name: 'City',
                        value: ''
                    }, {
                        name: 'CompanyName',
                        value: 'Monada'
                    }, {
                        name: 'OrganizationCode',
                        value: 'MONADATLV'
                    }]
                }, {
                    name: 'OrganizationAddress',
                    children: [/*{
                        name: 'CompanyName',
                        value: account.name,
                    },*/ {
                        name: 'AddressType',
                        value: dataTargetType === 'ForwardingShipment' ? 'LocalClient' : 'QuotationClientAddress',
                    }, {
                        name: 'Address1',
                        value: (account.address || {}).text || '',
                    }, {
                        name: 'AddressOverride',
                        value: 'false'
                    }, {
                        name: 'City',
                        value: 'XXXX'
                    }, {
                        name: 'Country',
                        children: [{
                            name: 'Code',
                            value: (account.address || {}).countryCode || '',
                        }, {
                            name: 'Name',
                            value: 'XXXX',
                        }]
                    }, {
                        name: 'OrganizationCode',
                        value: accountUniqueId,
                    }, {
                        name: 'Email',
                        value: account.contacts?.[0]?.email || '',
                    }]
                }]
            }, {
                name: 'ServiceLevel',
                children: [{
                    name: 'Code',
                    value: 'STD',
                }, {
                    name: 'Description',
                    value: 'Standard Service',
                }]
            }, {
                name: 'ShipmentIncoTerm',
                children: [{
                    name: 'Code',
                    value: opportunity.incoterm,
                }, {
                    name: 'Description',
                    value: INCOTERM_DESCRIPTION[opportunity.incoterm] || '',
                }]
            },
                ...addWeightsAndVolumes({ products, transportationMethod, organization }),
            {
                name: 'PackingLineCollection',
                attributes: [{ name: 'Content', value: 'Complete' }],
                children: _.flatMap(products, p => expandProductCalculations(p)).map(p => ({
                    name: 'PackingLine',
                    children: [{
                        name: 'PackQty',
                        value: p.quantity || 1,
                    },
                    ...addPackingLine({ product: p }),
                    ...addDangerousGoods({ product: p }),
                    {
                        name: 'PackType',
                        children: [{
                            name: 'Code',
                            value: NON_CONTAINERS.includes(p.type) ? 'PKG' : 'CTN',
                        }, {
                            name: 'Description',
                            value: NON_CONTAINERS.includes(p.type) ? 'Package' : 'Container',
                        }]
                    }]
                }))
            }, {

                name: 'ContainerCollection',
                attributes: [{ name: 'Content', value: 'Complete' }],
                children: products.filter(p => !(NON_CONTAINERS.includes(p.type))).map(p => ({
                    name: 'Container',
                    children: [{
                        name: 'ContainerCount',
                        value: p.quantity || 1
                    }, p.dangerous ? {
                        name: 'Commodity',
                        children: [{
                            name: 'Code',
                            value: 'HAZ',
                        }, {
                            name: 'Description',
                            value: 'HAZARDOUS GOODS',
                        }]
                    } : null, {
                        name: 'ContainerType',
                        children: [{
                            name: 'Code',
                            value: CONTAINER_TYPE[p.type]?.ContainerTypeCode || ''
                        }, {
                            name: 'Description',
                            value: CONTAINER_TYPE[p.type]?.ContainerTypeDescription || ''
                        }, {
                            name: 'Category',
                            children: [{
                                name: 'Code',
                                value: CONTAINER_TYPE[p.type]?.ContainerTypeCategoryCode || ''
                            }, {
                                name: 'Description',
                                value: CONTAINER_TYPE[p.type]?.ContainerTypeCategoryDescription || ''
                            }]
                        }]
                    }]
                })),
            }, {
                name: 'JobCosting',
                children: [{
                    name: 'ChargeLineCollection',
                    children: _.flatten(_.map(costs, (c, code) => _.map(c, (price, title) => ({
                        name: 'ChargeLine',
                        children: [{
                            name: 'ImportMetaData',
                            children: [{
                                name: 'Instruction',
                                value: 'Insert',
                            }, {
                                name: 'MatchingCriteriaCollection',
                                children: [{
                                    name: 'MatchingCriteria',
                                    children: [{
                                        name: 'FieldName',
                                        value: 'ChargeCode',
                                    }, {
                                        name: 'Value',
                                        value: code,
                                    }]
                                }]
                            }]
                        }, {
                            name: 'Branch',
                            children: [{
                                name: 'Code',
                                value: branchCode,
                            }, {
                                name: 'Name',
                                value: branchName,
                            }]
                        }, {
                            name: 'ChargeCode',
                            children: [{
                                name: 'Code',
                                value: code
                            }, {
                                name: 'Description',
                                value: title.split('|')[1]
                            }]
                        }, _.isEmpty(price.buying) ? {
                            name: 'CostOSAmount',
                            value: '0',
                        } : {
                            name: 'CostOSAmount',
                            value: (() => {
                                const currency = _.first(_.keys(price.buying));
                                return _.sum(_.map(price.buying, (p, c) => p / exchangeRates[c] * exchangeRates[currency])).toFixed(2);
                            })(),
                        }, _.isEmpty(price.buying) ? {
                            name: 'CostOSCurrency',
                            children: [{
                                name: 'Code',
                                value: _.first(_.keys(price.selling)) || 'USD'
                            }, {
                                name: 'Description',
                                value: CURRENCIES_NAME[_.first(_.keys(price.selling))] || 'US Dollar'
                            }]
                        } : {
                            name: 'CostOSCurrency',
                            children: [{
                                name: 'Code',
                                value: _.first(_.keys(price.buying)) || 'USD'
                            }, {
                                name: 'Description',
                                value: CURRENCIES_NAME[_.first(_.keys(price.buying))] || 'US Dollar'
                            }]
                        }, {
                            name: 'Creditor',
                            // Display-only: who this key was meant to be. A supplier with no
                            // uniqueId yields an empty Key, and the key alone can't say which
                            // supplier that was. Not a child/attribute, so parseStructureToXMLString
                            // never emits it — CargoWise still receives Type + Key only.
                            supplierName: (price.buyingCreditors || [])[0]?.name,
                            children: [{
                                name: 'Type',
                                value: 'Organization'
                            }, {
                                name: 'Key',
                                value: ((price.buyingCreditors || []).length === 0) ? 'MONADATLV' : _.last((price.buyingCreditors[0].uniqueId || '').split(':')),
                            }],
                        }, {
                            name: 'Debtor',
                            children: [{
                                name: 'Type',
                                value: 'Organization'
                            }, {
                                name: 'Key',
                                value: accountUniqueId || account.name,
                            }],
                        }, {
                            name: 'SellOSAmount',
                            value: price.selling ? price.selling[_.first(_.keys(price.selling))].toFixed(2) : '0',
                        }, {
                            name: 'SellOSCurrency',
                            children: [{
                                name: 'Code',
                                value: price.selling ? _.first(_.keys(price.selling)) : 'USD'
                            }, {
                                name: 'Description',
                                value: price.selling ? CURRENCIES_NAME[_.first(_.keys(price.selling))] : 'US Dollar'
                            }]
                        }, {
                            name: 'Department',
                            children: [{
                                name: 'Code',
                                value: (me.team || '').split(':')[1] || '',
                            }, {
                                name: 'Name',
                                value: (me.team || '').split(':')[0] || '',
                            }]
                        }, {
                            name: 'Description',
                            value: title.split('|')[1]
                        }]
                    }))))
                }]
            }])
        }]
    }

    return structure;
}

function checkHasAttributesOrValue(node) {
    if (!node) return false;
    if (node.value) return true;
    if (node.attributes) return true;
    if (node.children && node.children.length > 0) return _.some(node.children, checkHasAttributesOrValue);
    return false;
}

function parseStructureToXMLString(node, indent = 0) {
    if (!checkHasAttributesOrValue(node)) return '';

    let s = (indent <= 0 ? '' : ' '.repeat(indent * 4)) + `<${node.name}` + (node.attributes || []).map(attr => ( ` ${attr.name}="${attr.value}"`)).join('') + '>' + (indent < 0 || !node.children ? '' : '\n');
    _.compact((node.children || [])).map(child => s = s + parseStructureToXMLString(child, indent < 0 ? indent : indent + 1));
    if (!node.children) {
        s = s + xmlescape('' + (node.value || ''));
    }
    s = s + (indent <= 0 || !node.children ? '' : ' '.repeat(indent * 4)) + `</${node.name}>` + (indent === -1 ? '' : '\n');

    return s;
}

function addWeightsAndVolumes({ products, transportationMethod, organization }) {

    const [ totalVolume, totalVolumeUnit ] = (() => {
        const c = _.compact(products.map(p => calcTotalVolume({ product: p, transportationMethod })));
        if (c.length === 0) return [ 0, null ];
        return [ _.sumBy(c, 'val').toFixed(2), c[0].postfix];
    })();

    const [ totalWeight, kgOrLbs ] = (() => {
        const c = products
            .filter(p => p.type === 'LCL' || p.type === 'Air Freight' || p.type === 'Air freight' || (p.type.startsWith('By Volume or Weight')) || (p.type.startsWith('By Dimensions or Weight')))
            .map(p => calcWeight({ product: p }));
        if (c.length === 0) return [ 0, null ];
        return [ _.sumBy(c, 'val').toFixed(2), c[0].postfix];
    })();

    const [ actualChargable, actualChargeableUnit ] = (() => {
        if (transportationMethod === 'air') {
            const c = _.compact(products.map(p => calcChargeableWeight({ product: p, transportationMethod, allProducts: products, round: organization.airRoundChargeableWeight })));
            if (c.length === 0) return [ 0, null ];
            return [_.sumBy(c, 'val').toFixed(2), c[0].postfix];
        } else {
            const _p = products
                .filter(p => p.type === 'LCL' || (p.type.startsWith('By Volume or Weight')) || (p.type.startsWith('By Dimensions or Weight')));
            const c = _.compact(_p.map(p => calcWeightOrMeasurment({ product: p, transportationMethod, allProducts: _p })));
            if (c.length === 0) return [ 0, null ];
            return [_.sumBy(c, 'val').toFixed(2), c[0].postfix];
        }
    })();

    return _.compact([
        ( !kgOrLbs ? null : {
            name: 'TotalWeight',
            value: totalWeight
        }),
        ( !kgOrLbs ? null : {
            name: 'TotalWeightUnit',
            children: [{
                name: 'Code',
                value: kgOrLbs === 'kg' ? 'KG' : 'LB',
            }, {
                name: 'Description',
                value: kgOrLbs === 'kg' ? 'Kilograms' : 'Pounds',
            }]
        }),
        ( !totalVolumeUnit ? null : {
            name: 'TotalVolume',
            value: totalVolume
        }), ( !totalVolumeUnit ? null : {
            name: 'TotalVolumeUnit',
            children: [{
                name: 'Code',
                value: totalVolumeUnit === 'cbm' ? 'M3' : 'CF',
            }, {
                name: 'Description',
                value: totalVolumeUnit === 'cbm' ? 'Cubic Meter' : 'Cubic Feet',
            }]
        }),
        ( !actualChargeableUnit ? null : {
            name: 'ActualChargeable',
            value: actualChargable,
        }), ( !actualChargeableUnit ? null : {
            name: 'ActualChargeableUnit',
            children: [{
                name: 'Code',
                value: actualChargeableUnit === 'cbm' ? 'M3' : actualChargeableUnit === 'kg' ? 'KG' : 'T',
            }, {
                name: 'Description',
                value: actualChargeableUnit === 'cbm' ? 'Cubic Meter' : actualChargeableUnit === 'kg' ? 'Kilograms' : 'Tonnes',
            }]
        }),
        ( products.find(p => p.type === 'By Dimensions or Weight (sea)' || p.type === 'By Volume or Weight (sea)' || p.type === 'LCL') ? {
            name: 'OuterPacks',
            value: _.sumBy(
                _.flatMap(products.filter(p => p.type === 'By Dimensions or Weight (sea)' || p.type === 'By Volume or Weight (sea)' || p.type === 'LCL' || p.type === 'Air Freight' || p.type === 'Air freight'), p => expandProductCalculations(p)),
                p => p.quantity || 1
            ),
        } : null),
    ]);
}

// Expand unified LCL/Air freight products (with calculations[]) into one synthetic
// flat product per calculation so each calculation gets its own PackingLine entry.
function expandProductCalculations(product) {
    if ((product.type !== 'LCL' && product.type !== 'Air freight') || !product.calculations?.length) {
        return [product];
    }
    return product.calculations.map(calc => {
        const base = {
            id: calc.id,
            type: product.type,
            kgOrLbs: product.kgOrLbs || 'kg',
            cmOrInches: product.cmOrInches || 'cm',
            quantity: calc.quantity || 1,
            weight: calc.weight,
            dangerous: product.dangerous,
            dangerousClass: product.dangerousClass,
            dangerousUNCode: product.dangerousUNCode,
            dangerousMSDS: product.dangerousMSDS,
            commodity: calc.commodity || product.commodity,
        };
        if (calc.method === 'dimensions') {
            return { ...base, length: calc.length, width: calc.width, height: calc.height };
        }
        if (calc.method === 'volume') {
            return { ...base, volume: calc.volume };
        }
        // chargeable-weight
        return { ...base, chargeableWeight: calc.chargeableWeight };
    });
}

function addPackingLine({ product }) {
    if (product.type === 'By Dimensions or Weight (sea)' || product.type === 'By Dimensions or Weight (air)' || (product.type === 'LCL' && product.height) || ((product.type === 'Air Freight' || product.type === 'Air freight') && product.height)) {
        return [{
            name: 'Height',
            value: product.height,
        }, {
            name: 'Length',
            value: product.length,
        }, {
            name: 'Width',
            value: product.width,
        }, {
            name: 'LengthUnit',
            children: (product.cmOrInches || 'cm') === 'cm' ? [{
                name: 'Code',
                value: 'CM',
            }, {
                name: 'Description',
                value: 'Centimeters',
            }] : [{
                name: 'Code',
                value: 'IN',
            }, {
                name: 'Description',
                value: 'Inches',
            }]
        }, {
            name: 'Weight',
            value: product.weight,
        }, {
            name: 'WeightUnit',
            children: (product.kgOrLbs || 'kg') === 'kg' ? [{
                name: 'Code',
                value: 'KG',
            }, {
                name: 'Description',
                value: 'Kilograms',
            }] : [{
                name: 'Code',
                value: 'LB',
            }, {
                name: 'Description',
                value: 'Pounds',
            }]
        }];
    }
    if (product.type === 'By Volume or Weight (sea)' || product.type === 'LCL' || ((product.type === 'Air Freight' || product.type === 'Air freight') && product.volume)) {
        return [{
            name: 'Volume',
            value: product.volume,
        }, {
            name: 'VolumeUnit',
            children: (product.cmOrInches || 'cm') === 'cm' ? [{
                name: 'Code',
                value: 'M3',
            }, {
                name: 'Description',
                value: 'Cubic Meter',
            }] : [{
                name: 'Code',
                value: 'CF',
            }, {
                name: 'Description',
                value: 'Cubic Feet',
            }]
        }, {
            name: 'Weight',
            value: product.weight,
        }, {
            name: 'WeightUnit',
            children: (product.kgOrLbs || 'kg') === 'kg' ? [{
                name: 'Code',
                value: 'KG',
            }, {
                name: 'Description',
                value: 'Kilograms',
            }] : [{
                name: 'Code',
                value: 'LB',
            }, {
                name: 'Description',
                value: 'Pounds',
            }]
        }];
    }
    if (product.type === 'Air Freight' || product.type === 'Air freight') {
        return [{
            name: 'Weight',
            value: product.weight,
        }, {
            name: 'WeightUnit',
            children: (product.kgOrLbs || 'kg') === 'kg' ? [{
                name: 'Code',
                value: 'KG',
            }, {
                name: 'Description',
                value: 'Kilograms',
            }] : [{
                name: 'Code',
                value: 'LB',
            }, {
                name: 'Description',
                value: 'Pounds',
            }]
        }];
    }
    return [];
}

function addDangerousGoods({ product }) {
    if (!product.dangerous) return [];
    return [{
        name: 'Commodity',
        children: [{
            name: 'Code',
            value: 'HAZ',
        }, {
            name: 'Description',
            value: 'HAZARDOUS GOODS',
        }]
    }, {
        name: 'UNDGCollection',
        children: [{
            name: 'UNDG',
            children: [{
                name: 'IMOClass',
                value: getClassNumber({ dangerousClass: product.dangerousClass }),
            }, {
                name: 'UNDGCode',
                value: product.dangerousUNCode,
            }]
        }]
    }]
}

const TRANSPORT_MODE_CODE = {
    sea: 'SEA',
    air: 'AIR',
    land: 'ROA',
};

const TRANSPORT_MODE_DESCRIPTION = {
    sea: 'Sea Freight',
    air: 'Air Freight',
    land: 'Road Freight',
};

const CONTAINER_MODES = {
    sea: [{
        code: 'FCL',
        description: 'Full Container Load',
    }, {
        code: 'LCL',
        description: 'Less Container Load',
    }],
    air: [{
        code: 'LSE',
        description: 'Loose',
    }],
    land: [{
        code: 'FTL',
        description: 'Full Truck Load',
    }, {
        code: 'LTL',
        description: 'Less Truck Load',
    }],
};

const CONTAINER_TYPE = {
    '20\' Dry': {
        ContainerTypeCode: '20GP',
        ContainerTypeDescription: 'Twenty foot general purpose',
        ContainerTypeCategoryCode: 'Dry',
        ContainerTypeCategoryDescription: 'Dry Storage',
    },
    '20\' Flat': {
        ContainerTypeCode: '20FR',
        ContainerTypeDescription: 'Twenty foot flatrack',
        ContainerTypeCategoryCode: 'FLT',
        ContainerTypeCategoryDescription: 'Flat Rack',
    },
    '20\' Open Top': {
        ContainerTypeCode: '20OT',
        ContainerTypeDescription: 'Twenty foot open top',
        ContainerTypeCategoryCode: 'TOP',
        ContainerTypeCategoryDescription: 'Open Top',
    },
    '20\' Reefer': {
        ContainerTypeCode: '20RE',
        ContainerTypeDescription: 'Twenty foot reefer',
        ContainerTypeCategoryCode: 'RFG',
        ContainerTypeCategoryDescription: 'Refrigerated',
    },
    '40\' Dry': {
        ContainerTypeCode: '40GP',
        ContainerTypeDescription: 'Forty foot general purpose',
        ContainerTypeCategoryCode: 'Dry',
        ContainerTypeCategoryDescription: 'Dry Storage',
    },
    '40\' Flat': {
        ContainerTypeCode: '40FR',
        ContainerTypeDescription: 'Forty foot flatrack',
        ContainerTypeCategoryCode: 'FLT',
        ContainerTypeCategoryDescription: 'Flat Rack',
    },
    '40\' Open Top': {
        ContainerTypeCode: '40OT',
        ContainerTypeDescription: 'Forty foot open top',
        ContainerTypeCategoryCode: 'TOP',
        ContainerTypeCategoryDescription: 'Open Top',
    },
    '40\' Reefer': {
        ContainerTypeCode: '40RE',
        ContainerTypeDescription: 'Forty foot reefer',
        ContainerTypeCategoryCode: 'RFG',
        ContainerTypeCategoryDescription: 'Refrigerated',
    },
    '40\' HC Dry': {
        ContainerTypeCode: '40HC',
        ContainerTypeDescription: 'Forty foot high cube',
        ContainerTypeCategoryCode: 'DRY',
        ContainerTypeCategoryDescription: 'Dry Storage',
    },
    '40\' HC Flat': {
        ContainerTypeCode: '40FRHC',
        ContainerTypeDescription: 'Forty foot high cube flatrack',
        ContainerTypeCategoryCode: 'FLT',
        ContainerTypeCategoryDescription: 'Flat Rack',
    },
    '40\' HC Open Top': {
        ContainerTypeCode: '40OTHC',
        ContainerTypeDescription: 'Forty foot high cube open top',
        ContainerTypeCategoryCode: 'TOP',
        ContainerTypeCategoryDescription: 'Open Top',
    },
    '40\' HC Reefer': {
        ContainerTypeCode: '40REHC',
        ContainerTypeDescription: 'Forty foot high cube reefer',
        ContainerTypeCategoryCode: 'RFG',
        ContainerTypeCategoryDescription: 'Refrigerated',
    },
    '45\' HC Dry': {
        ContainerTypeCode: '45HC',
        ContainerTypeDescription: 'Forty five foot high cube',
        ContainerTypeCategoryCode: 'DRY',
        ContainerTypeCategoryDescription: 'Dry Storage',
    },
    '45\' HC Reefer': {
        ContainerTypeCode: '45REHC',
        ContainerTypeDescription: 'Forty five foot high cube reefer',
        ContainerTypeCategoryCode: 'RFG',
        ContainerTypeCategoryDescription: 'Refrigerated',
    },
}

const INCOTERM_DESCRIPTION = {
    EXW: 'Ex Works',
    FCA: 'FCA - Free Carrier (seller is responsible for origin, buyer for loading)',
    CPT: 'Carriage Paid To',
    CIP: 'Carriage and Insurance Paid To',
    DAP: 'Delivered At Place',
    DPU: 'Delivered at Place Unloaded',
    DDP: 'Delivered Duty Paid',
    FAS: 'Free Alongside Ship ',
    FOB: 'Free On Board',
    CFR: 'Cost And Freight',
    CIF: 'Cost, Insurance And Freight',
};

const NON_CONTAINERS = [
    'Air Freight',
    'Air freight',
    'By Dimensions or Weight (sea)',
    'By Volume or Weight (sea)',
    'LCL',
    'By Dimensions',
];


// A number pasted into an amount cell of the Send to CargoWise table. Drops currency signs and
// letters ("US$", "AUD"). A comma is a thousands separator when the text also has a dot
// ("1,234.50") or is grouped in threes ("1,234,567"); otherwise it is the decimal mark ("12,5").
// Not a number -> null.
function parsePastedAmount(text) {
    let t = (text || '').replace(/[^\d.,-]/g, '');
    if (t.includes('.') || /^-?\d{1,3}(,\d{3})+$/.test(t)) t = t.replace(/,/g, '');
    else t = t.replace(',', '.');
    return t !== '' && Number.isFinite(Number(t)) ? t : null;
}

export { createXmlStructureFromOpportunity, parseStructureToXMLString, parsePastedAmount, CONTAINER_MODES };
